<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "reka-ui";
import { useRouter } from "vue-router";
import { computed, ref, watch } from "vue";

import { caseService } from "@/services";
import { isApiError } from "@/services/Api";
import { $can } from "@/services/UtilitiesService";
import { useBrandStore } from "@/stores";

withDefaults(defineProps<{ casename: string }>(), {  })
const emit = defineEmits<(e: "deleted") => void>();
defineSlots<{ default: () => any }>();

const router = useRouter();

const deleteReason = ref("");

const isDeleting = ref(false);
const errorMessage = ref("");

const canDeleteCase = computed(() => $can("deleteCase"));

const selectedBrand = computed(() => useBrandStore().selectedBrand);
const selectedClient = computed(() => useBrandStore().selectedClient);

async function handleSubmit() {
  if (deleteReason.value.length < 1) return;

  isDeleting.value = false;
  errorMessage.value = "";

  if (selectedBrand.value == null || selectedClient.value == null) return;

  try {
    isDeleting.value = true;
    await caseService.deleteCase(selectedClient.value, selectedBrand.value, __props.casename, deleteReason.value);
    emit("deleted");
  } catch (e) {
    console.error(e);
    errorMessage.value =
      (isApiError<{ reason?: string }>(e) && (e.status === 406 || e.status === 423) ? e.data?.reason : undefined) ??
      "Error deleting case.";
  } finally {
    isDeleting.value = false;
  }
}

watch(
  canDeleteCase,
  () => {
    if (canDeleteCase.value) return;
    router.push({ name: "brand", params: { brand: selectedBrand.value } });
  },
  { immediate: true },
);
</script>
<template>
  <DialogRoot>
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogPortal>
      <DialogOverlay class="tw-fixed tw-inset-0 tw-z-[1000000] tw-h-full tw-w-full tw-bg-black/30" />
      <DialogContent
        :aria-describedby="undefined"
        class="tw-fixed tw-inset-0 tw-z-[1000000] tw-flex tw-items-center tw-justify-center"
      >
        <form
          class="tw-flex tw-max-w-lg tw-flex-col tw-gap-y-6 tw-rounded-lg tw-border tw-bg-white tw-p-4 tw-shadow-lg"
          @submit.prevent="handleSubmit"
        >
          <DialogTitle class="tw-text-balance tw-text-center tw-text-lg tw-font-semibold">
            Delete Case "{{ casename }}"
          </DialogTitle>
          <textarea
            v-model="deleteReason"
            required
            name="reason"
            class="tw-h-16 tw-w-full tw-resize-none tw-rounded-md tw-border tw-border-black tw-p-2"
            placeholder="Reason for Deleting (required)"
          />
          <div class="tw-flex tw-items-center tw-justify-start tw-gap-2">
            <p class="tw-text-sm tw-text-red-600 empty:tw-hidden">{{ errorMessage }}</p>
            <div class="tw-flex-auto" />
            <DialogClose class="tw-btn-md-green tw-min-w-20">Cancel</DialogClose>
            <button type="submit" class="tw-btn-md-gray tw-min-w-20">
              <i v-if="isDeleting" class="fas fa-spinner tw-animate-spin" />
              Delete
            </button>
          </div>
        </form>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
